import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../lib/api';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { toast } from 'sonner';
import { 
  Save, Plus, Trash2, Link2, ExternalLink, Loader2,
  GripVertical, Globe, FileText, Edit2, Eye, Compass, MapIcon, LinkIcon
} from 'lucide-react';

export default function FooterTab({ settings, onRefresh }) {
  const [loading, setLoading] = useState(false);
  const [availablePages, setAvailablePages] = useState([]);
  const [showCreatePage, setShowCreatePage] = useState(false);
  const [newPage, setNewPage] = useState({ title: '', slug: '', content: '' });
  const [creatingPage, setCreatingPage] = useState(false);
  const [editingPage, setEditingPage] = useState(null);
  const [formData, setFormData] = useState({
    footer_brand_description: '',
    footer_support_links: [],
    footer_explore_links: [],
    footer_copyright: '',
    // Show/Hide toggles
    show_footer_support: true,
    show_footer_explore: true,
    show_footer_contact: true,
    show_footer_social: true,
  });

  useEffect(() => {
    if (settings) {
      setFormData({
        footer_brand_description: settings.footer_brand_description || 
          "Discover the magic of Puerto Rico with our curated tours. From rainforest adventures to bioluminescent bays, we bring you the best of the island.",
        footer_support_links: settings.footer_support_links || [
          { label: "FAQ", url: "/faq" },
          { label: "Cancellation Policy", url: "/cancellation-policy" },
          { label: "Privacy Policy", url: "/privacy-policy" },
          { label: "Terms of Service", url: "/terms-of-service" }
        ],
        footer_explore_links: settings.footer_explore_links || [
          { label: "Adventure Tours", url: "/tours?category=adventure" },
          { label: "Marine Tours", url: "/tours?category=marine" },
          { label: "Food Tours", url: "/tours?category=food" },
          { label: "Night Tours", url: "/tours?category=night" },
          { label: "Tour Deals", url: "/tours?deals=true" }
        ],
        footer_copyright: settings.footer_copyright || "Tour Finder Puerto Rico. All rights reserved.",
        // Show/Hide toggles
        show_footer_support: settings.show_footer_support !== false,
        show_footer_explore: settings.show_footer_explore !== false,
        show_footer_contact: settings.show_footer_contact !== false,
        show_footer_social: settings.show_footer_social !== false,
      });
    }
    // Load available pages
    loadAvailablePages();
  }, [settings]);

  const loadAvailablePages = async () => {
    try {
      const response = await adminAPI.getAllPages();
      setAvailablePages(response.data || []);
    } catch (error) {
      console.error('Error loading pages:', error);
    }
  };

  const handleCreatePage = async () => {
    if (!newPage.title.trim()) {
      toast.error('Please enter a page title');
      return;
    }
    
    setCreatingPage(true);
    try {
      // Generate slug from title if not provided
      const slug = newPage.slug.trim() || newPage.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
      
      await adminAPI.createPage({
        title: newPage.title,
        slug: slug,
        content: newPage.content || `<h1>${newPage.title}</h1>\n<p>Enter your content here...</p>`,
        is_active: true
      });
      
      toast.success('Page created successfully!');
      setShowCreatePage(false);
      setNewPage({ title: '', slug: '', content: '' });
      loadAvailablePages();
      
      // Automatically add to support links
      setFormData({
        ...formData,
        footer_support_links: [
          ...formData.footer_support_links,
          { label: newPage.title, url: `/page/${slug}` }
        ]
      });
    } catch (error) {
      toast.error('Failed to create page');
    } finally {
      setCreatingPage(false);
    }
  };

  const handleDeletePage = async (pageId) => {
    if (!confirm('Are you sure you want to delete this page?')) return;
    
    try {
      await adminAPI.deletePage(pageId);
      toast.success('Page deleted');
      loadAvailablePages();
    } catch (error) {
      toast.error('Failed to delete page');
    }
  };

  const handleUpdatePage = async () => {
    if (!editingPage) return;
    
    try {
      await adminAPI.updatePage(editingPage.id, {
        title: editingPage.title,
        content: editingPage.content,
        is_active: editingPage.is_active
      });
      toast.success('Page updated successfully!');
      setEditingPage(null);
      loadAvailablePages();
    } catch (error) {
      toast.error('Failed to update page');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await adminAPI.updateSettings(formData);
      toast.success('Footer settings updated!');
      onRefresh();
    } catch (error) {
      console.error('Error saving footer settings:', error);
      toast.error('Failed to save footer settings');
    } finally {
      setLoading(false);
    }
  };

  const addSupportLink = () => {
    setFormData({
      ...formData,
      footer_support_links: [...formData.footer_support_links, { label: "", url: "" }]
    });
  };

  const updateSupportLink = (index, field, value) => {
    const newLinks = [...formData.footer_support_links];
    newLinks[index] = { ...newLinks[index], [field]: value };
    setFormData({ ...formData, footer_support_links: newLinks });
  };

  const removeSupportLink = (index) => {
    const newLinks = formData.footer_support_links.filter((_, i) => i !== index);
    setFormData({ ...formData, footer_support_links: newLinks });
  };

  const addExploreLink = () => {
    setFormData({
      ...formData,
      footer_explore_links: [...formData.footer_explore_links, { label: "", url: "" }]
    });
  };

  const updateExploreLink = (index, field, value) => {
    const newLinks = [...formData.footer_explore_links];
    newLinks[index] = { ...newLinks[index], [field]: value };
    setFormData({ ...formData, footer_explore_links: newLinks });
  };

  const removeExploreLink = (index) => {
    const newLinks = formData.footer_explore_links.filter((_, i) => i !== index);
    setFormData({ ...formData, footer_explore_links: newLinks });
  };

  return (
    <div data-testid="footer-tab">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl font-bold">Edit Footer</h1>
          <p className="text-muted-foreground">Customize the footer content displayed on your website</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Section Visibility Toggles */}
        <Card className="p-6">
          <h2 className="font-heading text-xl font-semibold mb-4 flex items-center gap-2">
            <Eye className="h-5 w-5 text-primary" />
            Footer Section Visibility
          </h2>
          <p className="text-sm text-muted-foreground mb-4">
            Control which sections are visible in the footer
          </p>
          
          <div className="grid md:grid-cols-2 gap-4">
            <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
              <div>
                <Label className="text-base font-medium">Explore Section</Label>
                <p className="text-sm text-muted-foreground">Show tour category links</p>
              </div>
              <Switch
                checked={formData.show_footer_explore}
                onCheckedChange={(checked) => setFormData({ ...formData, show_footer_explore: checked })}
                data-testid="toggle-footer-explore"
              />
            </div>
            
            <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
              <div>
                <Label className="text-base font-medium">Support Section</Label>
                <p className="text-sm text-muted-foreground">Show support/legal links</p>
              </div>
              <Switch
                checked={formData.show_footer_support}
                onCheckedChange={(checked) => setFormData({ ...formData, show_footer_support: checked })}
                data-testid="toggle-footer-support"
              />
            </div>
            
            <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
              <div>
                <Label className="text-base font-medium">Contact Section</Label>
                <p className="text-sm text-muted-foreground">Show address, phone & email</p>
              </div>
              <Switch
                checked={formData.show_footer_contact}
                onCheckedChange={(checked) => setFormData({ ...formData, show_footer_contact: checked })}
                data-testid="toggle-footer-contact"
              />
            </div>
            
            <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
              <div>
                <Label className="text-base font-medium">Social Media Icons</Label>
                <p className="text-sm text-muted-foreground">Show social media links</p>
              </div>
              <Switch
                checked={formData.show_footer_social}
                onCheckedChange={(checked) => setFormData({ ...formData, show_footer_social: checked })}
                data-testid="toggle-footer-social"
              />
            </div>
          </div>
        </Card>

        {/* Brand Description */}
        <Card className="p-6">
          <h2 className="font-heading text-xl font-semibold mb-4 flex items-center gap-2">
            <Compass className="h-5 w-5 text-primary" />
            Brand Description
          </h2>
          <p className="text-sm text-muted-foreground mb-4">
            This description appears under your logo in the footer
          </p>
          <textarea
            value={formData.footer_brand_description}
            onChange={(e) => setFormData({ ...formData, footer_brand_description: e.target.value })}
            placeholder="Enter your brand description..."
            className="w-full px-4 py-3 border rounded-lg resize-none"
            rows={4}
            data-testid="footer-brand-description"
          />
        </Card>

        {/* Explore Links */}
        {formData.show_footer_explore && (
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="font-heading text-xl font-semibold flex items-center gap-2">
                  <MapIcon className="h-5 w-5 text-primary" />
                  Explore Links
                </h2>
                <p className="text-sm text-muted-foreground">
                  Links displayed in the "Explore" section of your footer
                </p>
              </div>
              <Button type="button" variant="outline" onClick={addExploreLink} className="gap-2">
                <Plus className="h-4 w-4" />
                Add Link
              </Button>
            </div>

            <div className="space-y-3">
              {formData.footer_explore_links.map((link, index) => (
                <div key={index} className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg">
                  <div className="flex-1 grid grid-cols-2 gap-3">
                    <Input
                      value={link.label}
                      onChange={(e) => updateExploreLink(index, 'label', e.target.value)}
                      placeholder="Link Label (e.g., Adventure Tours)"
                      data-testid={`explore-link-label-${index}`}
                    />
                    <Input
                      value={link.url}
                      onChange={(e) => updateExploreLink(index, 'url', e.target.value)}
                      placeholder="URL (e.g., /tours?category=adventure)"
                      data-testid={`explore-link-url-${index}`}
                    />
                  </div>
                  <Button 
                    type="button"
                    variant="ghost" 
                    size="sm"
                    className="text-destructive hover:text-destructive"
                    onClick={() => removeExploreLink(index)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              {formData.footer_explore_links.length === 0 && (
                <p className="text-center text-muted-foreground py-4">
                  No explore links added. Click "Add Link" to create one.
                </p>
              )}
            </div>
          </Card>
        )}

        {/* Support Links */}
        {formData.show_footer_support && (
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="font-heading text-xl font-semibold flex items-center gap-2">
                  <LinkIcon className="h-5 w-5 text-primary" />
                  Support Links
                </h2>
                <p className="text-sm text-muted-foreground">
                  Links displayed in the "Support" section of your footer
                </p>
              </div>
              <Button type="button" variant="outline" onClick={addSupportLink} className="gap-2">
                <Plus className="h-4 w-4" />
                Add Link
              </Button>
            </div>

            {/* Quick Add from Custom Pages */}
            {availablePages.length > 0 && (
              <div className="mb-4 p-3 bg-blue-50 rounded-lg">
                <p className="text-sm font-medium text-blue-900 mb-2">Quick Add from Custom Pages:</p>
                <div className="flex flex-wrap gap-2">
                  {availablePages.filter(p => p.is_active).map((page) => {
                    const isAlreadyAdded = formData.footer_support_links.some(
                      link => link.url === `/page/${page.slug}`
                    );
                    return (
                      <Button
                        key={page.id}
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={isAlreadyAdded}
                        onClick={() => {
                          setFormData({
                            ...formData,
                            footer_support_links: [
                              ...formData.footer_support_links,
                              { label: page.title, url: `/page/${page.slug}` }
                            ]
                          });
                        }}
                        className={`text-xs ${isAlreadyAdded ? 'opacity-50' : 'bg-white'}`}
                      >
                        {isAlreadyAdded ? '✓ ' : '+ '}{page.title}
                      </Button>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="space-y-3">
              {formData.footer_support_links.map((link, index) => (
                <div key={index} className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg">
                  <div className="flex-1 grid grid-cols-2 gap-3">
                    <Input
                      value={link.label}
                      onChange={(e) => updateSupportLink(index, 'label', e.target.value)}
                      placeholder="Link Label (e.g., FAQ)"
                      data-testid={`support-link-label-${index}`}
                    />
                    <Input
                      value={link.url}
                      onChange={(e) => updateSupportLink(index, 'url', e.target.value)}
                      placeholder="URL (e.g., /page/faq)"
                      data-testid={`support-link-url-${index}`}
                    />
                  </div>
                  <Button 
                    type="button"
                    variant="ghost" 
                    size="sm"
                    className="text-destructive hover:text-destructive"
                    onClick={() => removeSupportLink(index)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              {formData.footer_support_links.length === 0 && (
                <p className="text-center text-muted-foreground py-4">
                  No support links added. Click "Add Link" to create one.
                </p>
              )}
            </div>
          </Card>
        )}

        {/* Create & Manage Custom Pages */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="font-heading text-xl font-semibold flex items-center gap-2">
                <FileText className="h-5 w-5 text-primary" />
                Custom Support Pages
              </h2>
              <p className="text-sm text-muted-foreground">
                Create and manage custom pages for FAQ, Terms, Privacy Policy, etc.
              </p>
            </div>
            <Button type="button" onClick={() => setShowCreatePage(true)} className="gap-2">
              <Plus className="h-4 w-4" />
              Create New Page
            </Button>
          </div>

          {/* Existing Pages List */}
          {availablePages.length > 0 ? (
            <div className="space-y-3">
              {availablePages.map((page) => (
                <div key={page.id} className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${page.is_active ? 'bg-green-100' : 'bg-gray-100'}`}>
                      <FileText className={`h-4 w-4 ${page.is_active ? 'text-green-600' : 'text-gray-500'}`} />
                    </div>
                    <div>
                      <p className="font-medium">{page.title}</p>
                      <p className="text-xs text-muted-foreground">/page/{page.slug}</p>
                    </div>
                    {page.is_active ? (
                      <Badge className="bg-green-100 text-green-700">Active</Badge>
                    ) : (
                      <Badge variant="secondary">Inactive</Badge>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => window.open(`/page/${page.slug}`, '_blank')}
                    >
                      <ExternalLink className="h-4 w-4" />
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setEditingPage({...page})}
                    >
                      <Edit2 className="h-4 w-4" />
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="text-red-600 hover:text-red-700"
                      onClick={() => handleDeletePage(page.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground border-2 border-dashed rounded-lg">
              <FileText className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p className="font-medium">No custom pages created yet</p>
              <p className="text-sm">Create pages for FAQ, Terms of Service, Privacy Policy, etc.</p>
              <Button type="button" onClick={() => setShowCreatePage(true)} className="mt-4 gap-2">
                <Plus className="h-4 w-4" />
                Create Your First Page
              </Button>
            </div>
          )}
        </Card>

        {/* Copyright Text */}
        <Card className="p-6">
          <h2 className="font-heading text-xl font-semibold mb-4 flex items-center gap-2">
            <FileText className="h-5 w-5 text-primary" />
            Copyright Text
          </h2>
          <p className="text-sm text-muted-foreground mb-4">
            This text appears at the bottom of the footer (© [year] will be added automatically)
          </p>
          <Input
            value={formData.footer_copyright}
            onChange={(e) => setFormData({ ...formData, footer_copyright: e.target.value })}
            placeholder="Your Company Name. All rights reserved."
            data-testid="footer-copyright"
          />
        </Card>

        {/* Preview */}
        <Card className="p-6 bg-foreground text-white">
          <h2 className="font-heading text-xl font-semibold mb-4">Preview</h2>
          <div className="border-t border-white/20 pt-4">
            <p className="text-gray-400 text-sm mb-4">{formData.footer_brand_description}</p>
            
            {formData.show_footer_explore && formData.footer_explore_links.length > 0 && (
              <div className="mb-4">
                <p className="text-white font-semibold text-sm mb-2">Explore</p>
                <div className="flex flex-wrap gap-4">
                  {formData.footer_explore_links.map((link, index) => (
                    <span key={index} className="text-gray-300 text-sm hover:text-secondary cursor-pointer">
                      {link.label || 'Untitled Link'}
                    </span>
                  ))}
                </div>
              </div>
            )}
            
            {formData.show_footer_support && formData.footer_support_links.length > 0 && (
              <div className="mb-4">
                <p className="text-white font-semibold text-sm mb-2">Support</p>
                <div className="flex flex-wrap gap-4">
                  {formData.footer_support_links.map((link, index) => (
                    <span key={index} className="text-gray-300 text-sm hover:text-secondary cursor-pointer">
                      {link.label || 'Untitled Link'}
                    </span>
                  ))}
                </div>
              </div>
            )}
            
            <p className="text-gray-500 text-sm mt-4 pt-4 border-t border-white/20">
              © {new Date().getFullYear()} {formData.footer_copyright}
            </p>
          </div>
        </Card>

        {/* Save Button */}
        <div className="flex justify-end">
          <Button type="submit" disabled={loading} className="gap-2" data-testid="save-footer-btn">
            {loading && <Loader2 className="h-4 w-4 animate-spin" />}
            Save Footer Settings
          </Button>
        </div>
      </form>

      {/* Create Page Dialog */}
      <Dialog open={showCreatePage} onOpenChange={setShowCreatePage}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Create New Support Page
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Page Title *</Label>
              <Input
                value={newPage.title}
                onChange={(e) => setNewPage({ ...newPage, title: e.target.value })}
                placeholder="e.g., Frequently Asked Questions"
              />
            </div>
            
            <div>
              <Label>URL Slug</Label>
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground text-sm">/page/</span>
                <Input
                  value={newPage.slug}
                  onChange={(e) => setNewPage({ ...newPage, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                  placeholder="faq"
                />
              </div>
              <p className="text-xs text-muted-foreground mt-1">Leave blank to auto-generate from title</p>
            </div>
            
            <div>
              <Label>Page Content (HTML)</Label>
              <Textarea
                value={newPage.content}
                onChange={(e) => setNewPage({ ...newPage, content: e.target.value })}
                placeholder="<h1>Page Title</h1>\n<p>Your content here...</p>"
                className="min-h-[200px] font-mono text-sm"
              />
              <p className="text-xs text-muted-foreground mt-1">
                You can use HTML tags for formatting. Supports headings, paragraphs, lists, etc.
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreatePage(false)}>Cancel</Button>
            <Button onClick={handleCreatePage} disabled={creatingPage} className="gap-2">
              {creatingPage && <Loader2 className="h-4 w-4 animate-spin" />}
              Create Page
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Page Dialog */}
      <Dialog open={!!editingPage} onOpenChange={() => setEditingPage(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit2 className="h-5 w-5" />
              Edit Page: {editingPage?.title}
            </DialogTitle>
          </DialogHeader>
          
          {editingPage && (
            <div className="space-y-4">
              <div>
                <Label>Page Title</Label>
                <Input
                  value={editingPage.title}
                  onChange={(e) => setEditingPage({ ...editingPage, title: e.target.value })}
                />
              </div>
              
              <div>
                <Label>Page Content (HTML)</Label>
                <Textarea
                  value={editingPage.content}
                  onChange={(e) => setEditingPage({ ...editingPage, content: e.target.value })}
                  className="min-h-[300px] font-mono text-sm"
                />
              </div>
              
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="page-active"
                  checked={editingPage.is_active}
                  onChange={(e) => setEditingPage({ ...editingPage, is_active: e.target.checked })}
                  className="h-4 w-4"
                />
                <label htmlFor="page-active" className="text-sm">Page is active and visible</label>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingPage(null)}>Cancel</Button>
            <Button onClick={handleUpdatePage} className="gap-2">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
